import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Package, ChevronDown, ChevronUp, DollarSign, AlertCircle, Layers, List, Settings, Download, Upload, FileSpreadsheet, Check, X } from 'lucide-react';
import { formatCostPrice } from '../hooks/useCurrency';
import { Header } from '../components/Header';

import { API_BASE } from '../lib/apiBase';
const API_URL = API_BASE;

// Storage key for grouping preference
const GROUPING_PREF_KEY = 'vinylflow_catalog_grouping';

export default function CatalogDetailPage() {
  const { catalogId } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showEditCatalog, setShowEditCatalog] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Grouping toggle - load from localStorage
  const [groupByCategory, setGroupByCategory] = useState(() => {
    try {
      return localStorage.getItem(GROUPING_PREF_KEY) === 'true';
    } catch {
      return false;
    }
  });
  
  // Categories for typeahead
  const [categories, setCategories] = useState([]);

  const fetchCatalog = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/catalogs/${catalogId}?_t=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('Failed to fetch catalog');
      const data = await res.json();
      setCatalog(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [catalogId]);
  
  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/catalogs/${catalogId}/categories?_t=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store',
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [catalogId]);

  useEffect(() => {
    fetchCatalog();
    fetchCategories();
  }, [fetchCatalog, fetchCategories]);
  
  // Save grouping preference to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(GROUPING_PREF_KEY, groupByCategory.toString());
    } catch {
      // Ignore localStorage errors
    }
  }, [groupByCategory]);
  
  // Group items by category
  const groupedItems = useMemo(() => {
    if (!catalog?.items) return { groups: [], ungrouped: [] };
    
    const groups = {};
    const ungrouped = [];
    
    catalog.items.forEach(item => {
      const cat = item.category;
      if (cat) {
        const catLower = cat.toLowerCase();
        if (!groups[catLower]) {
          groups[catLower] = { name: cat, items: [] };
        }
        groups[catLower].items.push(item);
      } else {
        ungrouped.push(item);
      }
    });
    
    // Sort groups alphabetically
    const sortedGroups = Object.values(groups).sort((a, b) => 
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
    
    return { groups: sortedGroups, ungrouped };
  }, [catalog?.items]);
  
  const hasCategories = groupedItems.groups.length > 0;
  
  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName.toLowerCase()]: !prev[categoryName.toLowerCase()]
    }));
  };

  // Expand-all / Collapse-all helpers. Default state is COLLAPSED (empty
  // map) so users don't get a wall of 20+ open categories on large rate
  // cards; these actions let them flip the whole set with one click.
  const expandAllCategories = () => {
    const next = {};
    (groupedItems?.groups || []).forEach(g => {
      next[g.name.toLowerCase()] = true;
    });
    if ((groupedItems?.ungrouped?.length || 0) > 0) {
      next['__other__'] = true;
    }
    setExpandedCategories(next);
  };
  const collapseAllCategories = () => {
    setExpandedCategories({});
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this price item?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchCatalog();
        fetchCategories();
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };
  
  const handleItemSaved = () => {
    setShowAddItem(false);
    setEditingItem(null);
    fetchCatalog();
    fetchCategories();
  };
  
  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pricing/catalogs/${catalogId}/export?format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.split('filename=')[1] || `catalog.${format}`;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed: ' + err.message);
    }
  };
  
  const handleDownloadTemplate = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pricing/catalogs/${catalogId}/export/template?format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Template download failed');
      
      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.split('filename=')[1] || `template.${format}`;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Template download failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="catalog-detail-page">
      <Header />
      {/* Page title + actions — the old back button lived here; global
          Admin Header now handles cross-section navigation. */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{catalog?.name}</h1>
              <p className="text-sm text-gray-500">{catalog?.partner_name || 'No supplier'} · {catalog?.currency}</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Export Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Export Items"
                >
                  <Download className="w-4 h-4" />
                  Export
                  <ChevronDown className="w-3 h-3" />
                </button>
                <div className="absolute right-0 mt-1 w-48 bg-white border border-border rounded-lg shadow-premium-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Export as CSV
                  </button>
                  <button
                    onClick={() => handleExport('xlsx')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Export as Excel
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={() => handleDownloadTemplate('xlsx')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-500 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Template
                  </button>
                </div>
              </div>
              
              {/* Import Button */}
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                title="Import Items"
              >
                <Upload className="w-4 h-4" />
                Import
              </button>
              
              <button
                onClick={() => setShowEditCatalog(true)}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                title="Edit Catalog"
              >
                <Settings className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowAddItem(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg border border-border p-4">
            <div className="text-sm text-gray-500 mb-1">Total Items</div>
            <div className="text-2xl font-semibold text-gray-900">{catalog?.items?.length || 0}</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-4">
            <div className="text-sm text-gray-500 mb-1">Categories</div>
            <div className="text-2xl font-semibold text-gray-900">{groupedItems.groups.length}</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-4">
            <div className="text-sm text-gray-500 mb-1">Currency</div>
            <div className="text-2xl font-semibold text-gray-900">{catalog?.currency}</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-4">
            <div className="text-sm text-gray-500 mb-1">Supplier</div>
            <div className="text-2xl font-semibold text-gray-900 truncate">{catalog?.partner_name}</div>
          </div>
        </div>
        
        {/* View Toggle */}
        {hasCategories && (
          <div className="flex items-center justify-end gap-3 mb-4">
            {/* Expand/Collapse all — only relevant in Group by Category
                mode. Default state on switching to Group mode is
                collapsed (users open only the categories they need). */}
            {groupByCategory && (
              <div className="flex items-center gap-1 text-sm">
                <button
                  onClick={expandAllCategories}
                  className="px-2.5 py-1.5 rounded text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                  data-testid="catalog-expand-all"
                >
                  Expand all
                </button>
                <span className="text-gray-300">·</span>
                <button
                  onClick={collapseAllCategories}
                  className="px-2.5 py-1.5 rounded text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                  data-testid="catalog-collapse-all"
                >
                  Collapse all
                </button>
              </div>
            )}
            <div className="flex items-center gap-2 bg-white border border-border rounded-lg p-1">
              <button
                onClick={() => setGroupByCategory(false)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  !groupByCategory ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <List className="w-4 h-4" />
                Flat List
              </button>
              <button
                onClick={() => setGroupByCategory(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  groupByCategory ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Layers className="w-4 h-4" />
                Group by Category
              </button>
            </div>
          </div>
        )}

        {/* Items List */}
        {(!catalog?.items || catalog.items.length === 0) ? (
          <div className="bg-white rounded-lg border border-border p-12 text-center">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No price items yet</h3>
            <p className="text-gray-500 mb-4">Add your first item to this catalog</p>
            <button
              onClick={() => setShowAddItem(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </button>
          </div>
        ) : groupByCategory ? (
          /* Grouped View */
          <div className="space-y-4">
            {groupedItems.groups.map((group) => {
              const isExpanded = expandedCategories[group.name.toLowerCase()] === true;
              return (
                <div key={group.name} className="bg-white rounded-lg border border-border overflow-hidden">
                  <button
                    onClick={() => toggleCategory(group.name)}
                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Layers className="w-4 h-4 text-indigo-500" />
                      <span className="font-medium text-gray-900">{group.name}</span>
                      <span className="text-sm text-gray-500">({group.items.length} items)</span>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  {isExpanded && (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                          <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cost (Min)</th>
                          <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase">Sales (Min)</th>
                          <th className="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase">Quote Status</th>
                          <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {group.items.map((item) => (
                          <ItemRow 
                            key={item.id} 
                            item={item} 
                            catalog={catalog}
                            expandedItem={expandedItem}
                            setExpandedItem={setExpandedItem}
                            setEditingItem={setEditingItem}
                            handleDeleteItem={handleDeleteItem}
                          />
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
            
            {/* Uncategorized items */}
            {groupedItems.ungrouped.length > 0 && (
              <div className="bg-white rounded-lg border border-border overflow-hidden">
                <button
                  onClick={() => toggleCategory('__other__')}
                  className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-500">Other</span>
                    <span className="text-sm text-gray-400">({groupedItems.ungrouped.length} items)</span>
                  </div>
                  {expandedCategories['__other__'] === true ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {expandedCategories['__other__'] === true && (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                        <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                        <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cost (Min)</th>
                        <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase">Sales (Min)</th>
                        <th className="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase">Quote Status</th>
                        <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {groupedItems.ungrouped.map((item) => (
                        <ItemRow 
                          key={item.id} 
                          item={item} 
                          catalog={catalog}
                          expandedItem={expandedItem}
                          setExpandedItem={setExpandedItem}
                          setEditingItem={setEditingItem}
                          handleDeleteItem={handleDeleteItem}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Flat List View */
          <div className="bg-white rounded-lg border border-border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  {hasCategories && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost (Min)</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sales (Min)</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Quote Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {catalog.items.map((item) => (
                  <ItemRow 
                    key={item.id} 
                    item={item} 
                    catalog={catalog}
                    expandedItem={expandedItem}
                    setExpandedItem={setExpandedItem}
                    setEditingItem={setEditingItem}
                    handleDeleteItem={handleDeleteItem}
                    showCategory={hasCategories}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Item Modal */}
      {showAddItem && (
        <PriceItemModal
          catalogId={catalogId}
          currency={catalog?.currency}
          categories={categories}
          onClose={() => setShowAddItem(false)}
          onSave={handleItemSaved}
        />
      )}

      {/* Edit Item Modal */}
      {editingItem && (
        <PriceItemModal
          catalogId={catalogId}
          currency={catalog?.currency}
          categories={categories}
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={handleItemSaved}
        />
      )}
      
      {/* Edit Catalog Modal */}
      {showEditCatalog && (
        <EditCatalogModal
          catalog={catalog}
          onClose={() => setShowEditCatalog(false)}
          onSave={() => {
            setShowEditCatalog(false);
            fetchCatalog();
          }}
        />
      )}
      
      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          catalogId={catalogId}
          catalogName={catalog?.name}
          onClose={() => setShowImportModal(false)}
          onImportComplete={() => {
            setShowImportModal(false);
            fetchCatalog();
            fetchCategories();
          }}
        />
      )}
    </div>
  );
}

// Item Row Component
function ItemRow({ item, catalog, expandedItem, setExpandedItem, setEditingItem, handleDeleteItem, showCategory = false }) {
  const minTier = item.tiers?.reduce((min, t) => t.cost_price < min.cost_price ? t : min, item.tiers[0]);
  const isExpanded = expandedItem === item.id;
  const colSpan = showCategory ? 8 : 7;
  
  return (
    <React.Fragment>
      <tr className="hover:bg-gray-50">
        <td className="px-6 py-4">
          <button
            onClick={() => setExpandedItem(isExpanded ? null : item.id)}
            className="flex items-center gap-2 font-mono text-sm text-indigo-600 hover:text-indigo-800"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {item.sku}
          </button>
        </td>
        <td className="px-6 py-4 font-medium text-gray-900">{item.name}</td>
        {showCategory && (
          <td className="px-6 py-4 text-sm">
            {item.category ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                {item.category}
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </td>
        )}
        <td className="px-6 py-4 text-sm text-gray-500">{item.unit}</td>
        <td className="px-6 py-4 text-right text-sm font-medium">
          {formatCostPrice(minTier?.cost_price ?? 0, catalog.currency, { showSymbol: false, showCode: true })}
        </td>
        <td className="px-6 py-4 text-right text-sm font-medium text-green-600">
          {catalog.currency} {minTier?.calculated_sales_price?.toFixed(2)}
        </td>
        <td className="px-6 py-4 text-center">
          {item.is_quotable ? (
            // ``is_quotable=true`` means NO quote needed (the admin's
            // "Requires Supplier Quote" checkbox is unchecked). Keep
            // a neutral slate pill rather than the old green "Yes" so
            // the status column reads as a status, not a yes/no.
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
              Normal pricing
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Quote Required
            </span>
          )}
        </td>
        <td className="px-6 py-4 text-right">
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setEditingItem(item)}
              className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteItem(item.id)}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={colSpan} className="px-6 py-4 bg-gray-50">
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <span className="text-gray-500">Markup:</span>
                <span className="font-medium">{item.effective_markup_percent}%</span>
                {item.category && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-500">Category: <span className="font-medium">{item.category}</span></span>
                  </>
                )}
                {item.description && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-500">{item.description}</span>
                  </>
                )}
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Price Tiers</h4>
                <div className="grid grid-cols-4 gap-2">
                  {item.tiers.map((tier, idx) => (
                    <div key={idx} className="bg-white border border-border rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">
                        {tier.min_qty}-{tier.max_qty || '∞'} units
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600">Cost:</span>{' '}
                        <span className="font-medium">{formatCostPrice(tier.cost_price, catalog.currency, { showSymbol: false, showCode: true })}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600">Sales:</span>{' '}
                        <span className="font-medium text-green-600">{catalog.currency} {tier.calculated_sales_price.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function PriceItemModal({ catalogId, currency, categories = [], item, onClose, onSave }) {
  const [formData, setFormData] = useState({
    sku: item?.sku || '',
    name: item?.name || '',
    description: item?.description || '',
    category: item?.category || '',
    unit: item?.unit || 'piece',
    is_quotable: item?.is_quotable ?? true,
    markup_percent: item?.markup_percent || '',
    tiers: item?.tiers?.map(t => ({
      min_qty: t.min_qty,
      max_qty: t.max_qty,
      cost_price: t.cost_price,
      sales_price: t.sales_price
    })) || [{ min_qty: 1, max_qty: null, cost_price: '', sales_price: '' }]
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const units = ['piece', 'unit', 'hour', 'page', 'kg', 'meter', 'set', 'lot'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const url = item
        ? `${API_URL}/api/pricing/items/${item.id}`
        : `${API_URL}/api/pricing/items`;
      
      const payload = {
        catalog_id: catalogId,
        sku: formData.sku,
        name: formData.name,
        description: formData.description || null,
        category: formData.category || null,
        unit: formData.unit,
        is_quotable: formData.is_quotable,
        markup_percent: formData.markup_percent ? parseFloat(formData.markup_percent) : null,
        tiers: formData.tiers.map(t => ({
          min_qty: parseInt(t.min_qty) || 1,
          max_qty: t.max_qty ? parseInt(t.max_qty) : null,
          cost_price: parseFloat(t.cost_price) || 0,
          sales_price: t.sales_price ? parseFloat(t.sales_price) : null
        }))
      };

      const res = await fetch(url, {
        method: item ? 'PATCH' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save item');
      }

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addTier = () => {
    const lastTier = formData.tiers[formData.tiers.length - 1];
    const newMinQty = lastTier?.max_qty ? lastTier.max_qty + 1 : 1;
    setFormData({
      ...formData,
      tiers: [...formData.tiers, { min_qty: newMinQty, max_qty: null, cost_price: '', sales_price: '' }]
    });
  };

  const removeTier = (index) => {
    if (formData.tiers.length <= 1) return;
    setFormData({
      ...formData,
      tiers: formData.tiers.filter((_, i) => i !== index)
    });
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...formData.tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setFormData({ ...formData, tiers: newTiers });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-gray-900">
            {item ? 'Edit Price Item' : 'Add Price Item'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="VINYL-12-BLACK"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unit *</label>
              <select
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {units.map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="12 inch Black Vinyl"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input
                type="text"
                list="category-suggestions"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g., Vinyl Production"
              />
              <datalist id="category-suggestions">
                {categories.map(cat => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
              <p className="text-xs text-gray-500 mt-1">Used for grouping items in catalog view</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Optional description"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Markup % (optional)</label>
              <input
                type="number"
                value={formData.markup_percent}
                onChange={(e) => setFormData({ ...formData, markup_percent: e.target.value })}
                step="0.1"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Leave empty for default"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  // Admin-facing wording is positive ("Requires Supplier
                  // Quote" — checked = needs a quote). The stored flag
                  // stays ``is_quotable`` (false = requires quote) so no
                  // schema or pricing-engine change is needed; we just
                  // invert the checked<->stored mapping at the edge.
                  checked={!formData.is_quotable}
                  onChange={(e) => setFormData({ ...formData, is_quotable: !e.target.checked })}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  data-testid="requires-supplier-quote-checkbox"
                />
                <span className="text-sm text-gray-700">Requires Supplier Quote</span>
              </label>
            </div>
          </div>

          {/* Price Tiers */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">Price Tiers *</label>
              <button
                type="button"
                onClick={addTier}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                + Add Tier
              </button>
            </div>
            <div className="space-y-3">
              {formData.tiers.map((tier, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Min Qty</label>
                      <input
                        type="number"
                        value={tier.min_qty}
                        onChange={(e) => updateTier(index, 'min_qty', e.target.value)}
                        min="1"
                        required
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Max Qty</label>
                      <input
                        type="number"
                        value={tier.max_qty || ''}
                        onChange={(e) => updateTier(index, 'max_qty', e.target.value || null)}
                        min="1"
                        placeholder="∞"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Cost ({currency})</label>
                      <input
                        type="number"
                        value={tier.cost_price}
                        onChange={(e) => updateTier(index, 'cost_price', e.target.value)}
                        step="0.0001"
                        min="0"
                        required
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                        data-testid={`price-tier-cost-input-${index}`}
                      />
                      <p className="text-[10px] text-gray-400 mt-0.5">Up to 4 decimals</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Sales Override</label>
                      <input
                        type="number"
                        value={tier.sales_price || ''}
                        onChange={(e) => updateTier(index, 'sales_price', e.target.value || null)}
                        step="0.01"
                        min="0"
                        placeholder="Auto"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                  {formData.tiers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTier(index)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (item ? 'Update Item' : 'Create Item')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Catalog Modal Component
function EditCatalogModal({ catalog, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: catalog?.name || '',
    description: catalog?.description || '',
    partner_id: catalog?.partner_id || ''
  });
  const [partners, setPartners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch available partners
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/partners`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPartners(data);
        }
      } catch (err) {
        console.error('Failed to fetch partners:', err);
      }
    };
    fetchPartners();
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/catalogs/${catalog.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          partner_id: formData.partner_id || null
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update catalog');
      }
      
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-gray-900">Edit Catalog</h2>
          <p className="text-sm text-gray-500 mt-1">
            Update catalog name and supplier. Currency ({catalog?.currency}) cannot be changed.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Catalog Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., Vinyl Production 2025"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Optional description"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Supplier / Partner</label>
            <select
              value={formData.partner_id}
              onChange={(e) => setFormData({ ...formData, partner_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">No supplier assigned</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>{partner.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Changing supplier only affects this catalog&apos;s association. Existing purchases/jobs are unaffected.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-500">
              <strong>Currency:</strong> {catalog?.currency} <span className="text-gray-400">(immutable)</span>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Import Modal Component
function ImportModal({ catalogId, catalogName, onClose, onImportComplete }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('update'); // 'update' or 'add_only'
  const [deleteMissing, setDeleteMissing] = useState(false);
  const [step, setStep] = useState('upload'); // 'upload', 'preview', 'importing'
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const name = droppedFile.name.toLowerCase();
      if (name.endsWith('.csv') || name.endsWith('.xlsx') || name.endsWith('.xls')) {
        setFile(droppedFile);
        setError(null);
      } else {
        setError('Please upload a CSV or Excel file');
      }
    }
  };
  
  const handlePreview = async () => {
    if (!file) return;
    
    setError(null);
    setStep('preview');
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(
        `${API_URL}/api/pricing/catalogs/${catalogId}/import/preview?mode=${mode}&delete_missing=${deleteMissing}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Preview failed');
      }
      
      const data = await response.json();
      setPreview(data);
    } catch (err) {
      setError(err.message);
      setStep('upload');
    }
  };
  
  const handleImport = async () => {
    if (!preview?.import_data) return;
    
    setImporting(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/pricing/catalogs/${catalogId}/import/commit`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(preview.import_data)
        }
      );
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Import failed');
      }
      
      onImportComplete();
    } catch (err) {
      setError(err.message);
      setImporting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Import Items</h2>
            <p className="text-sm text-gray-500">Import price items from CSV or Excel file</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}
          
          {step === 'upload' && (
            <div className="space-y-6">
              {/* File Upload */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  file ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-indigo-600" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      className="p-1 hover:bg-white rounded"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-1">Drop a file here or click to browse</p>
                    <p className="text-sm text-gray-400">Supports CSV and Excel (.xlsx)</p>
                  </>
                )}
              </div>
              
              {/* Import Options */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">Import Options</h3>
                
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="mode"
                      value="update"
                      checked={mode === 'update'}
                      onChange={() => setMode('update')}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Update existing + Add new</p>
                      <p className="text-sm text-gray-500">Update items by SKU, create new items if SKU doesn&apos;t exist</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="mode"
                      value="add_only"
                      checked={mode === 'add_only'}
                      onChange={() => setMode('add_only')}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Add new only</p>
                      <p className="text-sm text-gray-500">Skip items with existing SKUs, only add new items</p>
                    </div>
                  </label>
                </div>
                
                <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={deleteMissing}
                    onChange={(e) => setDeleteMissing(e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Delete missing items</p>
                    <p className="text-sm text-red-500">Remove items from catalog that are not in the import file</p>
                  </div>
                </label>
              </div>
            </div>
          )}
          
          {step === 'preview' && preview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-semibold text-gray-900">{preview.total_rows}</p>
                  <p className="text-xs text-gray-500">Total Rows</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-semibold text-green-600">{preview.to_add}</p>
                  <p className="text-xs text-green-600">To Add</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-semibold text-blue-600">{preview.to_update}</p>
                  <p className="text-xs text-blue-600">To Update</p>
                </div>
                {deleteMissing && (
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-semibold text-red-600">{preview.to_delete}</p>
                    <p className="text-xs text-red-600">To Delete</p>
                  </div>
                )}
              </div>
              
              {/* Errors */}
              {preview.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-800 mb-2">
                    Errors ({preview.errors.length})
                  </h4>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {preview.errors.map((err, idx) => (
                      <p key={idx} className="text-sm text-red-700">
                        Row {err.row}: {err.message} {err.field && `(${err.field})`}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Preview Items */}
              {preview.preview_items?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Preview Changes</h4>
                  <div className="border border-border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Action</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SKU</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Changes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {preview.preview_items.map((item, idx) => (
                          <tr key={idx} className={
                            item.action === 'add' ? 'bg-green-50' :
                            item.action === 'update' ? 'bg-blue-50' :
                            'bg-red-50'
                          }>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                item.action === 'add' ? 'bg-green-100 text-green-800' :
                                item.action === 'update' ? 'bg-blue-100 text-blue-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {item.action === 'add' ? 'Add' : item.action === 'update' ? 'Update' : 'Delete'}
                              </span>
                            </td>
                            <td className="px-3 py-2 font-mono">{item.sku}</td>
                            <td className="px-3 py-2">{item.name || item.new_data?.name}</td>
                            <td className="px-3 py-2 text-xs text-gray-500">
                              {item.changes && Object.keys(item.changes).map(key => (
                                <span key={key} className="mr-2">
                                  {key}: {String(item.changes[key].old)} → {String(item.changes[key].new)}
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-between items-center">
          {step === 'preview' && (
            <button
              onClick={() => { setStep('upload'); setPreview(null); }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              ← Back to upload
            </button>
          )}
          {step === 'upload' && <div />}
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            
            {step === 'upload' && (
              <button
                onClick={handlePreview}
                disabled={!file}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Preview Import
              </button>
            )}
            
            {step === 'preview' && preview && (
              <button
                onClick={handleImport}
                disabled={importing || (preview.to_add === 0 && preview.to_update === 0 && preview.to_delete === 0)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Confirm Import
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
